import React, {useCallback} from 'react';
import {Autocomplete, FormControl, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {SxProps} from '@mui/system';
import {CompanyInUserInfos} from '@/types/company';
import {FocusEvent} from 'react';

export interface AllCompaniesOption {
  id: {_id: null; title: string};
}

export type CompanyOption = CompanyInUserInfos | AllCompaniesOption;

export type CompanyInputProps = {
  companies: CompanyOption[];
  value: CompanyOption | CompanyOption[] | null;
  onChange: (
    event: React.SyntheticEvent,
    newValue: CompanyOption | CompanyOption[] | null
  ) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps;
  multiple?: boolean;
  error?: string | string[] | undefined;
  touched?: boolean;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  disabled?: boolean;
};

export const CompanyInput: React.FC<CompanyInputProps> = ({
  companies,
  value,
  onChange,
  label,
  placeholder,
  sx,
  multiple = false,
  touched,
  error,
  onBlur,
  disabled
}) => {
  const {t} = useTranslation();

  const handleCompanyChange = useCallback(
    (
      event: React.SyntheticEvent,
      newValue: CompanyOption | CompanyOption[] | null
    ) => {
      if (onChange) {
        onChange(event, newValue);
      }
    },
    [onChange]
  );

  return (
    <FormControl sx={{width: '100%', ...sx}} variant="outlined" fullWidth>
      <Autocomplete
        options={companies}
        getOptionLabel={(option) => option?.id?.title || 'option.title'}
        value={value}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        isOptionEqualToValue={(option, selectedValue) =>
          selectedValue != null && option.id._id === selectedValue.id._id
        }
        onChange={handleCompanyChange}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label || t('company')}
            placeholder={placeholder || t('choose_company')}
            fullWidth
            error={touched && Boolean(error)}
            helperText={touched && error}
            onBlur={onBlur}
          />
        )}
      />
    </FormControl>
  );
};
